import * as React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

import { Banner } from "@/components/ui/banner";
import { Button } from "@/components/ui/button";
import { ApiError } from "@/lib/api-client";
import {
  AUDIT_LOG_PAGE_SIZE,
  useQueryAuditLog,
  type AuditLogFilters,
} from "@/hooks/queries/useQueryAuditLog";
import {
  AuditLogFilterBar,
  EMPTY_AUDIT_FILTERS,
  type AuditFilterState,
} from "@/routes/admin/AuditLogFilterBar";
import { AuditLogTable } from "@/routes/admin/AuditLogTable";

// Story 11.7 (AC9/AC10) — the super_admin audit console, /admin/audit.
//
// Read-only and forensic: no write action anywhere on the view, so the design
// system's "one primary CTA per view" is satisfied by having zero. Tokens only,
// no pure-white background, no hardcoded hex (CLAUDE.md frontend constraints).

/** The error code the API uses for `since > until` — rendered ON the To field. */
const DATE_RANGE_ERROR_CODE = "ADMIN-010";

/**
 * The last instant a `timestamptz` can hold inside a day, not the last one a JS
 * `Date` can: Postgres stores `occurred_at` at microsecond resolution, so a row
 * stamped `…23:59:59.999500+00:00` is real and a `To` edge of `…999` excludes it.
 */
const END_OF_DAY_MICROSECONDS = ".999999Z";

/**
 * Convert a picked local calendar date into the ISO instant the API filters on.
 *
 * The picker gives a `yyyy-mm-dd` with no zone. Sending it raw would have the API
 * read it as UTC, which makes "today" wrong for every operator not on UTC; and
 * because the window is inclusive at both ends, `to` must be the END of its day or
 * a range picked as "1 Jan to 31 Jan" would silently drop the 31st.
 *
 * The `To` edge carries three digits more than `Date` does, and they are
 * load-bearing rather than pedantry: `query_audit_log` filters `occurred_at <=
 * until`, so a millisecond edge drops every sub-millisecond row of the last day in
 * the range. The range visibly covers that row's day and the console reports a
 * short count with no cue that anything was left out — the exact hole an inclusive
 * window exists to close. `Date` cannot hold the precision, so it is restored on
 * the SERIALIZED form, after `Date` has done the one job it is needed for here:
 * the local→UTC conversion, which leaves the millisecond field untouched because
 * zone offsets are whole minutes. If `toISOString` ever stops emitting its fixed
 * `.sssZ` shape the replace is a no-op, so the edge degrades to milliseconds
 * rather than to garbage.
 */
export function localDateToInstant(
  date: string,
  edge: "start" | "end",
): string | undefined {
  if (!date) return undefined;
  const suffix = edge === "start" ? "T00:00:00.000" : "T23:59:59.999";
  const parsed = new Date(`${date}${suffix}`);
  if (Number.isNaN(parsed.getTime())) return undefined;
  const instant = parsed.toISOString();
  if (edge === "start") return instant;
  return instant.replace(/\.\d{3}Z$/, END_OF_DAY_MICROSECONDS);
}

function toQueryFilters(filters: AuditFilterState, page: number): AuditLogFilters {
  return {
    page,
    actor_id: filters.actorId || undefined,
    event_name: filters.eventName || undefined,
    org_id: filters.orgId || undefined,
    since: localDateToInstant(filters.from, "start"),
    until: localDateToInstant(filters.to, "end"),
  };
}

export function AuditLogScreen() {
  const [filters, setFilters] = React.useState<AuditFilterState>(EMPTY_AUDIT_FILTERS);
  const [page, setPage] = React.useState(1);

  // Any filter change restarts at page 1 — page 4 of the previous filter is not a
  // meaningful position in a different result set.
  function applyFilters(next: AuditFilterState) {
    setFilters(next);
    setPage(1);
  }

  const query = useQueryAuditLog(toQueryFilters(filters, page));
  const { data, isPending, isFetching, isError, error, refetch } = query;

  const rows = data?.results ?? [];
  const count = data?.count ?? 0;
  const totalPages = Math.max(1, Math.ceil(count / AUDIT_LOG_PAGE_SIZE));

  // A refused date range belongs on the field the operator can fix, not in a page
  // banner — so it is pulled out of the error before the banner branch runs.
  const isRangeError = error instanceof ApiError && error.code === DATE_RANGE_ERROR_CODE;
  const dateRangeError = isRangeError
    ? "The start of the range is after its end. Swap the two dates."
    : undefined;

  return (
    <section className="flex flex-col gap-6">
      <header className="flex flex-col gap-1">
        <h1 className="display-xs text-[color:var(--text-primary)]">Audit log</h1>
        <p className="text-sm text-[color:var(--text-tertiary)]">
          Every recorded platform action, newest first. Entries are retained for seven
          years and are never edited or deleted.
        </p>
        {/*
          Story 16.9 AC16 / DW-157 (PM 2026-08-24). A SECOND paragraph rather than a
          longer first one — and NOT because merging would break a test. Both
          assertions on the retention sentence (`__tests__/AuditLogScreen.test.tsx`
          and `frontend/e2e/admin-audit-log.spec.ts`) use UNANCHORED regexes that
          match as substrings, so a merged paragraph would still satisfy them; see
          `AuditLogScreen.test.tsx`, "leaves the retention sub-line and the note as
          separate paragraphs", which states this at length and pins the split for
          the real reason. That reason is a reading one: the retention sentence is a
          durable property of the record, the note describes what the reader is
          seeing right now, and a reader who meets them as one block reads the
          second as a qualifier on the first.

          What it says, and why the console has to say it. Every successful page of
          this endpoint emits one `admin.audit_log.queried` row into the very table
          it is paging (`application/audit/query_audit_log/query.py`). Rows are
          ordered `sequence_number DESC` and the page is an OFFSET window, so the
          read's own row takes the highest sequence number and pushes the whole
          order down by exactly one: page 2 always repeats page 1's last row, and
          the total ticks up by one per page turn.

          That backend docstring is explicit that this is DOCUMENTED, not repaired
          — an offset page over an append-only DESC order provably repeats and
          never skips, so no row is ever hidden from an investigator, and the
          duplicate is self-evident (the same event id twice). Every mechanism fix
          is worse than the symptom, and each belongs to someone else: the keyset
          cursor is reserved for DW-68, and suppressing the meta-audit row is a
          product ruling about what the trail means, not a paging fix. So the
          screen is where the gap actually was — the behaviour was pinned in a
          Python docstring the operator will never read, while the console said
          nothing at all. AC16 changes no pagination behaviour on purpose; the
          defect is legibility, and prose is the whole of the fix.
        */}
        <p className="text-sm text-[color:var(--text-tertiary)]">
          Viewing this log is itself a recorded action, so new entries can arrive while
          you read. Pages are counted from the newest entry, so an entry you have
          already seen may appear again on the next page. Nothing is ever skipped.
        </p>
      </header>

      <AuditLogFilterBar
        filters={filters}
        onChange={applyFilters}
        dateRangeError={dateRangeError}
      />

      <div className="flex flex-col gap-3">
        <div className="flex items-baseline justify-between gap-4">
          <h2 className="text-lg font-semibold text-[color:var(--text-primary)]">
            Audit events
          </h2>
          <span className="text-sm text-[color:var(--text-tertiary)] [font-variant-numeric:tabular-nums]">
            {count} events
          </span>
        </div>

        {isError && !isRangeError ? (
          <Banner variant="danger" title="Couldn't load the audit log">
            <div className="flex flex-col gap-2">
              <span>We couldn&apos;t load the audit events. Please try again.</span>
              <span>
                <Button variant="secondary" size="sm" onClick={() => void refetch()}>
                  Try again
                </Button>
              </span>
            </div>
          </Banner>
        ) : null}

        {isPending ? (
          <div
            className="flex items-center justify-center rounded-none border border-[color:var(--border-primary)] bg-[color:var(--bg-primary)] px-6 py-12 text-sm text-[color:var(--text-tertiary)]"
            role="status"
            aria-live="polite"
            aria-busy="true"
          >
            Loading audit events…
          </div>
        ) : rows.length === 0 && !isError ? (
          <Banner variant="info" title="No matching audit events">
            No events were recorded for these filters. Widen the date range or clear a
            filter to see more.
          </Banner>
        ) : rows.length > 0 ? (
          <>
            <AuditLogTable rows={rows} />
            <nav className="flex items-center justify-between gap-4" aria-label="Pagination">
              <span className="text-sm text-[color:var(--text-tertiary)] [font-variant-numeric:tabular-nums]">
                Page {page} of {totalPages}
              </span>
              <div className="flex items-center gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={!data?.previous || isFetching}
                >
                  <ChevronLeft aria-hidden="true" className="h-4 w-4" />
                  Previous
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setPage((p) => p + 1)}
                  disabled={!data?.next || isFetching}
                >
                  Next
                  <ChevronRight aria-hidden="true" className="h-4 w-4" />
                </Button>
              </div>
            </nav>
          </>
        ) : null}
      </div>
    </section>
  );
}

export default AuditLogScreen;
